import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useAppDispatch } from '@/shared/hooks';
import { debug, delay, getCardRemovalAnimationDuration } from '@/shared/lib';

import { ModalID, modalModel } from '@/entities/modal';
import { studyModel } from '@/entities/study';

import { ReportCardLayout } from '@/features/reportCards';

import { useRawMeshViewerCard } from '../../hooks/useRawMeshViewerCard';
import { RawMeshViewerActionsPopover } from '../RawMeshViewerActionsPopover/RawMeshViewerActionsPopover';

type RawMeshViewerCardProps = {
  study: Study;
  className?: string;
  testID?: string;
};

export const RawMeshViewerCard: FC<RawMeshViewerCardProps> = (props) => {
  const { study, className, testID } = props;

  const dispatch = useAppDispatch();
  const { formatDate, formatMessage } = useIntl();
  const [isRemoving, setIsRemoving] = useState(false);
  const { title, uploadDate, previewSrc, handleCardClick } =
    useRawMeshViewerCard(study);
  const { CanDelete = false } = study.YourPermissions ?? {};

  const openRemoveConfirmModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage({
            id: 'removeReportModal.title',
            defaultMessage: 'Remove report',
          }),
          okText: formatMessage({
            id: 'global.applyRemove',
            defaultMessage: 'Yes, remove',
          }),
          description: formatMessage({
            id: 'removeReportModal.message',
            defaultMessage:
              'After removing, all report data will be lost. Are you sure?',
          }),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            setIsRemoving(true);

            try {
              await delay(getCardRemovalAnimationDuration());
              await dispatch(
                studyModel.thunks.unsetStudyRawMeshViewer({
                  StudyID: study.ID,
                }),
              ).unwrap();
            } catch (error) {
              setIsRemoving(false);
              debug('Failed to unset raw mesh viewer:', error);
            }
          },
        },
      }),
    );
  };

  const openInfoModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.RawMeshViewerInfo,
        data: {
          studyID: study.ID,
          canDelete: CanDelete,
          onRemove: openRemoveConfirmModal,
        },
      }),
    );
  };

  const handleMobileInfoClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    openInfoModal();
  };

  return (
    <ReportCardLayout
      data-component="raw-mesh-viewer-card"
      className={className}
      date={
        uploadDate ? formatDate(uploadDate, { dateStyle: 'medium' }) : undefined
      }
      desktopActions={
        <RawMeshViewerActionsPopover
          canDelete={CanDelete}
          onShowInfo={openInfoModal}
          onRemove={openRemoveConfirmModal}
        />
      }
      isGenerationCompleted
      isRemoving={isRemoving}
      onClick={isRemoving ? undefined : handleCardClick}
      onMobileActionsClick={handleMobileInfoClick}
      previewAlt={title}
      previewSrc={previewSrc}
      testID={testID}
      title={title}
    />
  );
};
